import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { supabase, storage } from '../lib/supabase'
import { useRecaptcha } from '../hooks/useRecaptcha'

// Validation schema
const libroReclamacionesSchema = z.object({
  // Información Personal
  nombres: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  apellido_paterno: z.string().min(2, 'El apellido paterno es requerido'),
  apellido_materno: z.string().min(2, 'El apellido materno es requerido'),
  email: z.string().email('Ingrese un email válido'),
  email_confirmacion: z.string().email('Ingrese un email válido'),
  dni_ce: z.string().min(8, 'DNI/CE debe tener al menos 8 caracteres'),
  domicilio: z.string().min(5, 'Ingrese un domicilio válido'),
  distrito: z.string().min(2, 'El distrito es requerido'),
  telefono: z.string().min(7, 'Ingrese un teléfono válido'),
  es_vecino_centro_comercial: z.boolean(),
  es_menor_edad: z.boolean(),
  
  // Información del Bien Contratado
  tipo_bien: z.enum(['Producto', 'Servicio']),
  descripcion_bien: z.string().min(10, 'Describa el producto o servicio (mínimo 10 caracteres)'),
  monto_reclamado: z.string().optional(),
  moneda_monto: z.enum(['Soles', 'Dólares']).optional(),
  
  // Detalles del Reclamo
  tipo_registro: z.enum(['Queja', 'Reclamo']),
  problema_principal: z.string().optional(),
  detalle_problema: z.string().min(20, 'Detalle el problema (mínimo 20 caracteres)'),
  
  // Pedido
  pedido: z.string().min(10, 'Indique su pedido (mínimo 10 caracteres)'),
}).refine((data) => data.email === data.email_confirmacion, {
  message: "Los emails no coinciden",
  path: ["email_confirmacion"],
})

export default function LibroReclamaciones() {
  const { verifyRecaptcha } = useRecaptcha()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [folio, setFolio] = useState(null)
  const sigCanvas = useRef(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: zodResolver(libroReclamacionesSchema),
    defaultValues: {
      es_vecino_centro_comercial: false,
      es_menor_edad: false,
      tipo_bien: 'Producto',
      tipo_registro: 'Reclamo',
      moneda_monto: 'Soles'
    }
  })

  const clearSignature = () => {
    sigCanvas.current?.clear()
  }

  const onSubmit = async (data) => {
    setLoading(true)
    setError(null)

    try {
      // Verify reCAPTCHA (non-blocking - only log errors)
      try {
        const recaptchaResult = await verifyRecaptcha('libro_reclamaciones')
        if (!recaptchaResult.success) {
          console.warn('reCAPTCHA verification failed:', recaptchaResult.error)
        }
      } catch (recaptchaError) {
        console.warn('reCAPTCHA error (non-critical):', recaptchaError)
      }

      // Get signature data
      const signatureData = sigCanvas.current?.toDataURL()

      if (!signatureData || sigCanvas.current?.isEmpty()) {
        setError('Por favor, firme el documento')
        setLoading(false)
        return
      }

      // Convert Base64 signature to Blob and upload to Storage (private bucket)
      const base64 = signatureData.split(',')[1]
      const byteString = atob(base64)
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
      const signatureBlob = new Blob([ab], { type: 'image/png' })

      const signaturePath = `firmas/${Date.now()}.png`
      const { error: sigUploadError } = await storage.uploadImage('reclamaciones', signatureBlob, signaturePath)
      
      // Prepare data for submission
      const submitData = {
        ...data,
        monto_reclamado: data.monto_reclamado ? parseFloat(data.monto_reclamado) : null,
        firma_digital: signatureData, // Keep base64 as fallback
      }

      // Save storage path if upload succeeded
      if (!sigUploadError) {
        submitData.firma_url = signaturePath
      } else {
        console.warn('Signature upload failed; keeping base64 in DB', sigUploadError)
      }

      // Insert into Supabase
      const { data: result, error: submitError } = await supabase
        .from('libro_reclamaciones')
        .insert([submitData])
        .select('folio')
        .single()

      if (submitError) throw submitError

      setFolio(result.folio)
      setSuccess(true)
      reset()
      clearSignature()
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Error al enviar reclamo:', err)
      setError('Ocurrió un error al enviar el reclamo. Por favor, intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Libro de Reclamaciones - Plaza Aurora</title>
        </Helmet>
        <div style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <div style={{
              background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
              border: '1px solid #6EE7B7',
              borderRadius: 'var(--radius-md)',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                color: '#059669',
                marginBottom: '1rem'
              }}>
                <svg style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto'
                }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#065F46',
                marginBottom: '0.5rem'
              }}>¡Reclamo Registrado Exitosamente!</h2>
              {folio && (
                <p style={{
                  fontSize: 'var(--font-size-h3)',
                  fontFamily: 'var(--font-ui)',
                  color: '#065F46',
                  marginBottom: '1rem'
                }}>
                  Su número de folio es: <span style={{ fontWeight: '700' }}>{folio}</span>
                </p>
              )}
              <p style={{
                fontSize: 'var(--font-size-body)',
                fontFamily: 'var(--font-ui)',
                color: '#065F46',
                marginBottom: '1.5rem'
              }}>
                Hemos recibido su reclamo. Le responderemos a la brevedad posible al correo electrónico proporcionado.
              </p>
              <button
                onClick={() => setSuccess(false)}
                style={{
                  padding: '0.875rem 2rem',
                  background: 'var(--ctaBg)',
                  color: 'var(--ctaText)',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: '600',
                  fontFamily: 'var(--font-ui)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  e.currentTarget.style.background = 'var(--brand-blue)'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                  e.currentTarget.style.background = 'var(--ctaBg)'
                  e.currentTarget.style.color = 'var(--ctaText)'
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                  e.currentTarget.style.background = 'var(--brand-blue-600)'
                }}
              >
                Registrar Otro Reclamo
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Libro de Reclamaciones - Plaza Aurora</title>
      </Helmet>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '4rem 2rem'
      }}>
        <div style={{ maxWidth: '896px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 style={{
              fontSize: 'var(--h1)',
              fontWeight: '700',
              fontFamily: 'var(--font-display)',
              color: 'var(--color-text-primary)',
              marginBottom: '1rem'
            }}>Libro de Reclamaciones</h1>
            <p style={{
              fontSize: 'var(--font-size-h3)',
              fontFamily: 'var(--font-ui)',
              color: 'var(--color-text-muted)'
            }}>
              Complete el siguiente formulario para registrar su queja o reclamo. Todos los campos marcados con (*) son obligatorios.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
              border: '1px solid #F87171',
              borderRadius: 'var(--radius-md)',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                fontSize: 'var(--font-size-body)',
                fontFamily: 'var(--font-ui)',
                color: '#991B1B'
              }}>{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}>
            {/* Sección 1: Información Personal */}
            <div className="libro-card">
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#111827',
                marginBottom: '1.5rem'
              }}>1. Información Personal</h2>
              
              <div className="libro-grid-2col">
                <div className="libro-full-width">
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Nombres *
                  </label>
                  <input
                    type="text"
                    {...register('nombres')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.nombres && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.nombres.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Apellido Paterno *
                  </label>
                  <input
                    type="text"
                    {...register('apellido_paterno')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.apellido_paterno && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.apellido_paterno.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Apellido Materno *
                  </label>
                  <input
                    type="text"
                    {...register('apellido_materno')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.apellido_materno && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.apellido_materno.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    DNI / C.E. *
                  </label>
                  <input
                    type="text"
                    {...register('dni_ce')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.dni_ce && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.dni_ce.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    {...register('telefono')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.telefono && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.telefono.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.email && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Confirmar Email *
                  </label>
                  <input
                    type="email"
                    {...register('email_confirmacion')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.email_confirmacion && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.email_confirmacion.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Domicilio *
                  </label>
                  <input
                    type="text"
                    {...register('domicilio')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.domicilio && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.domicilio.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Distrito *
                  </label>
                  <input
                    type="text"
                    {...register('distrito')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  />
                  {errors.distrito && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.distrito.message}</p>
                  )}
                </div>
              </div>

              {/* Checkboxes */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                marginTop: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <input
                    type="checkbox"
                    {...register('es_vecino_centro_comercial')}
                    style={{
                      width: '1rem',
                      height: '1rem',
                      color: '#2563eb',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-sm)',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  <label style={{
                    marginLeft: '0.5rem',
                    fontSize: 'var(--sm)',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    cursor: 'pointer'
                  }}>
                    Soy vecino del centro comercial
                  </label>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <input
                    type="checkbox"
                    {...register('es_menor_edad')}
                    style={{
                      width: '1rem',
                      height: '1rem',
                      color: '#2563eb',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-sm)',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  <label style={{
                    marginLeft: '0.5rem',
                    fontSize: 'var(--sm)',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    cursor: 'pointer'
                  }}>
                    Soy menor de edad
                  </label>
                </div>
              </div>
            </div>

            {/* Sección 2: Información del Bien Contratado */}
            <div className="libro-card">
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#111827',
                marginBottom: '1.5rem'
              }}>2. Información del Bien Contratado</h2>
              
              <div className="libro-grid-2col">
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Tipo de Bien *
                  </label>
                  <select
                    {...register('tipo_bien')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                  >
                    <option value="Producto">Producto</option>
                    <option value="Servicio">Servicio</option>
                  </select>
                  {errors.tipo_bien && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.tipo_bien.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Monto Reclamado
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem'
                  }}>
                    <input
                      type="number"
                      step="0.01"
                      {...register('monto_reclamado')}
                      style={{
                        flex: '1',
                        padding: '0.5rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: 'var(--radius-md)',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        '&:focus': {
                          border: '2px solid #6366f1',
                          outline: 'none'
                        }
                      }}
                      placeholder="0.00"
                    />
                    <select
                      {...register('moneda_monto')}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: 'var(--radius-md)',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        '&:focus': {
                          border: '2px solid #6366f1',
                          outline: 'none'
                        }
                      }}
                    >
                      <option value="Soles">S/</option>
                      <option value="Dólares">$</option>
                    </select>
                  </div>
                  {errors.monto_reclamado && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.monto_reclamado.message}</p>
                  )}
                </div>

                <div className="libro-full-width">
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Descripción del Producto o Servicio *
                  </label>
                  <textarea
                    {...register('descripcion_bien')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                    placeholder="Describa el producto o servicio adquirido"
                  />
                  {errors.descripcion_bien && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.descripcion_bien.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sección 3: Detalles del Reclamo */}
            <div className="libro-card">
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#111827',
                marginBottom: '1.5rem'
              }}>3. Detalles del Reclamo</h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.75rem'
                  }}>
                    Tipo de Registro *
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'start' }}>
                      <input
                        type="radio"
                        value="Reclamo"
                        {...register('tipo_registro')}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          color: '#2563eb',
                          border: '1px solid #d1d5db',
                          borderRadius: 'var(--radius-full)',
                          outline: 'none',
                          transition: 'all 0.2s ease',
                          '&:focus': {
                            border: '2px solid #6366f1',
                            outline: 'none'
                          },
                          marginTop: '0.25rem'
                        }}
                      />
                      <div style={{ marginLeft: '0.75rem' }}>
                        <label style={{
                          fontSize: 'var(--sm)',
                          fontWeight: '600',
                          fontFamily: 'var(--font-ui)',
                          color: '#111827',
                        }}>Reclamo</label>
                        <p style={{
                          fontSize: 'var(--xs)',
                          fontFamily: 'var(--font-ui)',
                          color: '#6b7280',
                        }}>Disconformidad relacionada a los productos o servicios</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'start' }}>
                      <input
                        type="radio"
                        value="Queja"
                        {...register('tipo_registro')}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          color: '#2563eb',
                          border: '1px solid #d1d5db',
                          borderRadius: 'var(--radius-full)',
                          outline: 'none',
                          transition: 'all 0.2s ease',
                          '&:focus': {
                            border: '2px solid #6366f1',
                            outline: 'none'
                          },
                          marginTop: '0.25rem'
                        }}
                      />
                      <div style={{ marginLeft: '0.75rem' }}>
                        <label style={{
                          fontSize: 'var(--sm)',
                          fontWeight: '600',
                          fontFamily: 'var(--font-ui)',
                          color: '#111827',
                        }}>Queja</label>
                        <p style={{
                          fontSize: 'var(--xs)',
                          fontFamily: 'var(--font-ui)',
                          color: '#6b7280',
                        }}>Disconformidad no relacionada a los productos o servicios, o malestar respecto a la atención al público</p>
                      </div>
                    </div>
                  </div>
                  {errors.tipo_registro && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.tipo_registro.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Problema Principal
                  </label>
                  <input
                    type="text"
                    {...register('problema_principal')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                    placeholder="Resuma el problema en una línea"
                  />
                  {errors.problema_principal && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.problema_principal.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Detalle del Problema *
                  </label>
                  <textarea
                    {...register('detalle_problema')}
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                    placeholder="Describa detalladamente el problema o motivo de su queja/reclamo"
                  />
                  {errors.detalle_problema && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.detalle_problema.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sección 4: Pedido y Firma */}
            <div className="libro-card">
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#111827',
                marginBottom: '1.5rem'
              }}>4. Pedido y Firma</h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Pedido *
                  </label>
                  <textarea
                    {...register('pedido')}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 'var(--radius-md)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        border: '2px solid #6366f1',
                        outline: 'none'
                      }
                    }}
                    placeholder="Indique qué solicita como solución a su reclamo o queja"
                  />
                  {errors.pedido && (
                    <p style={{
                      fontSize: 'var(--sm)',
                      fontFamily: 'var(--font-ui)',
                      color: '#ef4444',
                      marginTop: '0.25rem'
                    }}>{errors.pedido.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Firma Digital *
                  </label>
                  <div style={{
                    border: '2px solid #d1d5db',
                    borderRadius: 'var(--radius-md)',
                    padding: '0.5rem'
                  }}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-40 bg-white rounded',
                        style: { touchAction: 'none' }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearSignature}
                    style={{
                      marginTop: '0.5rem',
                      fontSize: 'var(--sm)',
                      color: '#1d4ed8',
                      cursor: 'pointer',
                      '&:hover': {
                        color: '#1e40af'
                      }
                    }}
                  >
                    Limpiar Firma
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '0.75rem 3rem',
                  background: 'var(--ctaBg)',
                  color: 'var(--ctaText)',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: '600',
                  fontFamily: 'var(--font-ui)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    e.currentTarget.style.background = 'var(--brand-blue)'
                    e.currentTarget.style.color = 'white'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                    e.currentTarget.style.background = 'var(--ctaBg)'
                    e.currentTarget.style.color = 'var(--ctaText)'
                  }
                }}
                onMouseDown={(e) => {
                  if (!loading) {
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                    e.currentTarget.style.background = 'var(--brand-blue-600)'
                  }
                }}
              >
                {loading ? 'Enviando...' : 'Enviar Reclamo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

